import React, { createContext, useContext, useEffect, useState } from 'react'
import { getApiBaseUrl } from '../utils/apiBaseUrl'

export interface User {
  id: string
  email: string
  first_name?: string
  last_name?: string
  name?: string
  has_subscription?: boolean
  subscription_tier?: string
  email_verified?: boolean
}

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string, recaptchaToken?: string) => Promise<void>
  register: (email: string, password: string, firstName: string, lastName: string, recaptchaToken?: string) => Promise<void>
  googleLogin: (idToken: string) => Promise<void>
  logout: () => void
  isAuthenticated: boolean
  refreshUser: () => Promise<void>
  requestPasswordReset: (email: string) => Promise<void>
  confirmPasswordReset: (token: string, newPassword: string) => Promise<void>
  sendVerificationEmail: () => Promise<void>
  verifyEmail: (token: string) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

const API_BASE_URL = getApiBaseUrl()

// Google Ads conversion tracking
// To get your conversion label ID: Google Ads > Tools & Settings > Conversions > Select your conversion action > Tag setup
// Update this with your conversion label ID in format: 'AW-725659273/XXXXX' where XXXXX is your label ID
const GOOGLE_ADS_CONVERSION_ID = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL || 'AW-725659273'

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check for existing session on app load
    checkAuthStatus()
  }, [])

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) {
        setLoading(false)
        return
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        const userData = await response.json()
        setUser(userData)
      } else {
        // Token is invalid, remove it
        localStorage.removeItem('auth_token')
      }
    } catch (error) {
      //console.error('Auth check failed:', error)
      localStorage.removeItem('auth_token')
    } finally {
      setLoading(false)
    }
  }

  const login = async (email: string, password: string, recaptchaToken?: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, recaptcha_token: recaptchaToken }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Login failed')
      }

      const data = await response.json()
      localStorage.setItem('auth_token', data.token)
      setUser(data.user)

      // Track Google Ads conversion for sign-in
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('event', 'conversion', {
          'send_to': GOOGLE_ADS_CONVERSION_ID,
        })
      }
    } catch (error) {
      throw error
    }
  }

  const register = async (email: string, password: string, firstName: string, lastName: string, recaptchaToken?: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, first_name: firstName, last_name: lastName, recaptcha_token: recaptchaToken }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Registration failed')
      }

      const data = await response.json()
      localStorage.setItem('auth_token', data.token)
      setUser(data.user)

      // Track Google Ads conversion for sign-up/sign-in
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('event', 'conversion', {
          'send_to': 'AW-725659273',
        })
      }
    } catch (error) {
      throw error
    }
  }

  const googleLogin = async (idToken: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id_token: idToken }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Google login failed')
      }

      const data = await response.json()
      localStorage.setItem('auth_token', data.token)
      setUser(data.user)

      // Track Google Ads conversion for sign-in
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('event', 'conversion', {
          'send_to': GOOGLE_ADS_CONVERSION_ID,
        })
      }
    } catch (error) {
      throw error
    }
  }

  const logout = () => {
    localStorage.removeItem('auth_token')
    setUser(null)
  }

  const requestPasswordReset = async (email: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/request-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Password reset request failed')
      }
    } catch (error) {
      throw error
    }
  }

  const confirmPasswordReset = async (token: string, newPassword: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/confirm-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, new_password: newPassword }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Password reset failed')
      }
    } catch (error) {
      throw error
    }
  }

  const sendVerificationEmail = async () => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/send-verification-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to send verification email')
      }
    } catch (error) {
      throw error
    }
  }

  const verifyEmail = async (token: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Email verification failed')
      }

      // Refresh user data after verification
      await checkAuthStatus()
    } catch (error) {
      throw error
    }
  }

  const refreshUser = async () => {
    await checkAuthStatus()
  }

  const value = {
    user,
    loading,
    login,
    register,
    googleLogin,
    logout,
    isAuthenticated: !!user,
    refreshUser,
    requestPasswordReset,
    confirmPasswordReset,
    sendVerificationEmail,
    verifyEmail,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}